(() => {
  const SELECTOR_ROOT = '[data-cart-drawer-root]';

  function parseBoolean(value) {
    return value === true || value === 'true' || value === '1';
  }

  function isElement(node) {
    return node && node.nodeType === 1;
  }

  function getCartChangeUrl() {
    const url = window.theme && window.theme.routes && window.theme.routes.cart_change_url;
    return url || '/cart/change';
  }

  function parseQty(value) {
    const raw = typeof value === 'string' ? value.trim() : String(value);
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.round(n));
  }

  function setLoadingState(node, isLoading) {
    if (!isElement(node)) return;

    const loadingClass = 'cart-drawer__loading';

    if (isLoading) {
      node.classList.add(loadingClass);
      node.setAttribute('aria-busy', 'true');

      if ('disabled' in node) {
        if (!node.hasAttribute('data-cart-drawer-was-disabled')) {
          node.setAttribute('data-cart-drawer-was-disabled', node.disabled ? '1' : '0');
        }
        node.disabled = true;
      }
    } else {
      node.classList.remove(loadingClass);
      node.removeAttribute('aria-busy');

      if ('disabled' in node) {
        const wasDisabled = node.getAttribute('data-cart-drawer-was-disabled');
        if (wasDisabled === '0') {
          node.disabled = false;
        }
        node.removeAttribute('data-cart-drawer-was-disabled');
      }
    }
  }

  function getPathnameFromUrl(url) {
    try {
      const u = new URL(String(url), window.location.origin);
      return u.pathname || '';
    } catch (e) {
      return '';
    }
  }

  function isCartMutationUrl(url) {
    const pathname = getPathnameFromUrl(url);
    if (!pathname) return false;

    const normalized = pathname.toLowerCase();
    return (
      normalized === '/cart/add' ||
      normalized === '/cart/add.js' ||
      normalized === '/cart/change' ||
      normalized === '/cart/change.js' ||
      normalized === '/cart/update' ||
      normalized === '/cart/update.js' ||
      normalized === '/cart/clear' ||
      normalized === '/cart/clear.js'
    );
  }

  function dispatchCartUpdated(detail) {
    try {
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: detail || null
        })
      );
    } catch (e) {
      // ignore
    }
  }

  function installCartInterceptors() {
    if (window.__cartDrawerCartInterceptorsInstalled) return;
    window.__cartDrawerCartInterceptorsInstalled = true;

    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      const open = window.XMLHttpRequest.prototype.open;
      window.XMLHttpRequest.prototype.open = function(method, url) {
        try {
          this.__cartDrawerUrl = url;
        } catch (e) {
          // ignore
        }

        this.addEventListener('load', function() {
          try {
            if (!isCartMutationUrl(this.__cartDrawerUrl)) return;

            let payload = null;
            try {
              const text = this.responseText;
              payload = text ? JSON.parse(text) : null;
            } catch (e) {
              payload = null;
            }

            dispatchCartUpdated(payload);
          } catch (e) {
            // ignore
          }
        });

        return open.apply(this, arguments);
      };
    }

    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch;
      window.fetch = async function(input, init) {
        const res = await originalFetch.apply(this, arguments);

        try {
          const url = input && typeof input === 'object' && 'url' in input ? input.url : input;
          if (isCartMutationUrl(url)) {
            const clone = res.clone();
            let payload = null;
            try {
              payload = await clone.json();
            } catch (e) {
              payload = null;
            }

            dispatchCartUpdated(payload);
          }
        } catch (e) {
          // ignore
        }

        return res;
      };
    }
  }

  class CartDrawer {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.renderUrl = root.dataset.renderUrl || '';
      this.openSelector = root.dataset.openSelector || '.cart-link';
      this.closeSelector = root.dataset.closeSelector || '[data-cart-drawer-close]';
      this.addFormSelector = root.dataset.addFormSelector || 'form[action^="/cart/add"]';
      this.cartCountSelector = root.dataset.cartCountSelector || '';
      this.openClass = root.dataset.openClass || 'is-open';
      this.openOnAdd = parseBoolean(root.dataset.openOnAdd);
      this.updateCount = parseBoolean(root.dataset.updateCount);

      this._openRefreshPromise = null;
      this._cartChangedRefreshPromise = null;
      this._qtyTimers = new Map();
      this._qtyUpdatePromise = null;
      this._suppressCartUpdated = 0;
      this._suppressThemeCartChanged = 0;
      this._lastItemCount = null;
      this._primeCartPromise = null;
      this._offerCarouselObserver = null;
      this._offerCarouselObservedEl = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentChange = this.onDocumentChange.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.onThemeCartChanged = this.onThemeCartChanged.bind(this);
      this.onCartUpdated = this.onCartUpdated.bind(this);

      this.bind();

      this.setupOfferCarouselObserver();

      this.primeCartState();
    }

    disconnectOfferCarouselObserver() {
      if (this._offerCarouselObserver) {
        try {
          this._offerCarouselObserver.disconnect();
        } catch (e) {
          // ignore
        }
      }
      this._offerCarouselObserver = null;
      this._offerCarouselObservedEl = null;
    }

    setupOfferCarouselObserver() {
      const content = this.root.querySelector('.cart-drawer__recommendations-content');
      if (!content) {
        this.disconnectOfferCarouselObserver();
        return;
      }

      if (this._offerCarouselObservedEl === content && this._offerCarouselObserver) {
        return;
      }

      this.disconnectOfferCarouselObserver();
      this._offerCarouselObservedEl = content;

      const applyEnable = () => {
        const hasCarousel = Boolean(content.querySelector('product-offer-carousel, .product-offer-carousel'));
        if (!hasCarousel) return;

        const wrap = content.closest('.cart-drawer__recommendations');
        if (!wrap) return;

        wrap.classList.add('enable-toggler');
      };

      applyEnable();

      this._offerCarouselObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (!mutation || !mutation.addedNodes) continue;

          for (const node of mutation.addedNodes) {
            if (!isElement(node)) continue;
            if (
              node.matches('product-offer-carousel, .product-offer-carousel') ||
              node.querySelector('product-offer-carousel, .product-offer-carousel')
            ) {
              applyEnable();
              return;
            }
          }
        }
      });

      this._offerCarouselObserver.observe(content, { childList: true, subtree: true });
    }

    primeCartState() {
      if (this._primeCartPromise) return this._primeCartPromise;

      this._primeCartPromise = (async () => {
        const cart = await this.fetchCart();
        this.refreshCartCountFromCart(cart);
      })();

      return this._primeCartPromise;
    }

    onQtyButtonClick(button) {
      if (!isElement(button)) return;

      const qtyWrap = button.closest('.cart-drawer__qty');
      const input = qtyWrap ? qtyWrap.querySelector('.cart-drawer__qty-input') : null;
      if (!input || !isElement(input)) return;

      const current = parseQty(input.value);
      const delta = parseQty(button.getAttribute('data-qty-change'));
      const deltaSigned = Number(button.getAttribute('data-qty-change'));
      if (current == null || !Number.isFinite(deltaSigned)) return;

      const next = Math.max(0, current + deltaSigned);
      input.value = String(next);
      this.queueQtyUpdate(input);
    }

    queueQtyUpdate(input) {
      if (!isElement(input)) return;

      const key = input.dataset.key || input.dataset.line || '';
      if (!key) return;

      const existing = this._qtyTimers.get(key);
      if (existing) {
        window.clearTimeout(existing);
      }

      const timer = window.setTimeout(() => {
        this._qtyTimers.delete(key);
        this.applyQtyFromInput(input);
      }, 350);

      this._qtyTimers.set(key, timer);
    }

    async applyQtyFromInput(input) {
      if (!isElement(input)) return;

      const key = input.dataset.key;
      const lineRaw = input.dataset.line;
      const line = Number(lineRaw);
      const hasValidLine = Boolean(line) && !Number.isNaN(line);
      const hasKey = Boolean(key);

      if (!hasKey && !hasValidLine) return;

      const qty = parseQty(input.value);
      if (qty == null) return;

      const qtyWrap = input.closest('.cart-drawer__qty');
      const buttons = qtyWrap ? qtyWrap.querySelectorAll('.cart-drawer__qty-btn') : [];

      const wasInputDisabled = input.disabled;
      input.disabled = true;
      buttons.forEach((b) => {
        if (isElement(b)) b.disabled = true;
      });

      if (qtyWrap) {
        setLoadingState(qtyWrap, true);
      }

      try {
        await this.updateLineItemQuantity({ key, line, hasKey, hasValidLine, quantity: qty });
      } catch (err) {
        console.error(err);
        document.documentElement.dispatchEvent(
          new CustomEvent('cart-drawer:error', { bubbles: true, detail: { error: err } })
        );
      } finally {
        if (qtyWrap) {
          setLoadingState(qtyWrap, false);
        }

        if (!wasInputDisabled) input.disabled = false;
        buttons.forEach((b) => {
          if (isElement(b)) b.disabled = false;
        });
      }
    }

    async onRecoAddClick(button) {
      if (!isElement(button)) return;

      const variantIdRaw = button.getAttribute('data-variant-id');
      const variantId = Number(variantIdRaw);
      if (!variantId || Number.isNaN(variantId)) return;

      setLoadingState(button, true);
      this._suppressCartUpdated += 1;
      this._suppressThemeCartChanged += 1;

      try {
        const body = new URLSearchParams();
        body.set('id', String(variantId));
        body.set('quantity', '1');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body
        });

        if (!res.ok) {
          let errorPayload = '';
          try {
            errorPayload = await res.text();
          } catch (e) {
            errorPayload = '';
          }
          throw new Error(`Reco add to cart failed: ${res.status}${errorPayload ? ` - ${errorPayload}` : ''}`);
        }

        await res.json();

        await this.refreshCartCount();
        await this.refreshSection();
      } catch (err) {
        console.error(err);
        document.documentElement.dispatchEvent(
          new CustomEvent('cart-drawer:error', { bubbles: true, detail: { error: err } })
        );
      } finally {
        this._suppressCartUpdated = Math.max(0, this._suppressCartUpdated - 1);
        this._suppressThemeCartChanged = Math.max(0, this._suppressThemeCartChanged - 1);
        setLoadingState(button, false);
      }
    }

    async updateLineItemQuantity({ key, line, hasKey, hasValidLine, quantity }) {
      if (this._qtyUpdatePromise) {
        try {
          await this._qtyUpdatePromise;
        } catch (e) {
          // ignore
        }
      }

      this._qtyUpdatePromise = (async () => {
        this._suppressCartUpdated += 1;
        this._suppressThemeCartChanged += 1;
        const baseUrl = getCartChangeUrl();
        const url = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;

        const body = new URLSearchParams();
        if (hasKey) {
          body.set('id', String(key));
        } else if (hasValidLine) {
          body.set('line', String(line));
        }
        body.set('quantity', String(quantity));

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body
        });

        if (!res.ok) {
          let errorPayload = '';
          try {
            errorPayload = await res.text();
          } catch (e) {
            errorPayload = '';
          }
          throw new Error(`Update quantity failed: ${res.status}${errorPayload ? ` - ${errorPayload}` : ''}`);
        }

        await res.json();

        await this.refreshCartCount();
        await this.refreshSection();
      })();

      try {
        await this._qtyUpdatePromise;
      } finally {
        this._suppressCartUpdated = Math.max(0, this._suppressCartUpdated - 1);
        this._suppressThemeCartChanged = Math.max(0, this._suppressThemeCartChanged - 1);
        this._qtyUpdatePromise = null;
      }
    }

    bind() {
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('change', this.onDocumentChange);
      document.addEventListener('input', this.onDocumentChange);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('submit', this.onFormSubmit);
      document.documentElement.addEventListener('theme:cartchanged', this.onThemeCartChanged);
      document.addEventListener('cart:updated', this.onCartUpdated);
    }

    destroy() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('change', this.onDocumentChange);
      document.removeEventListener('input', this.onDocumentChange);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('submit', this.onFormSubmit);
      document.documentElement.removeEventListener('theme:cartchanged', this.onThemeCartChanged);
      document.removeEventListener('cart:updated', this.onCartUpdated);

      this.disconnectOfferCarouselObserver();
    }

    onDocumentChange(evt) {
      const target = evt.target;
      if (!isElement(target)) return;

      if (!this.root.contains(target)) return;
      if (!target.matches('.cart-drawer__qty-input')) return;

      this.queueQtyUpdate(target);
    }

    open() {
      this.root.classList.add(this.openClass);
      document.body.classList.add('cart-drawer-is-open');
    }

    close() {
      this.root.classList.remove(this.openClass);
      document.body.classList.remove('cart-drawer-is-open');
    }

    toggle() {
      if (this.root.classList.contains(this.openClass)) {
        this.close();
      } else {
        this.open();
      }
    }

    async fetchCart() {
      try {
        const res = await fetch('/cart.js', {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          cache: 'no-store'
        });

        if (!res.ok) return null;
        return await res.json();
      } catch (err) {
        console.error(err);
        return null;
      }
    }

    refreshCartCountFromCart(cart) {
      if (!cart || typeof cart.item_count !== 'number') return;

      const prevCount = typeof this._lastItemCount === 'number' ? this._lastItemCount : null;
      this._lastItemCount = cart.item_count;

      if (prevCount != null && prevCount > 0 && cart.item_count === 0) {
        this.close();
      }

      if (!this.cartCountSelector) return;

      const nodes = document.querySelectorAll(this.cartCountSelector);
      nodes.forEach((node) => {
        if (!isElement(node)) return;

        if (cart.item_count > 0) {
          node.textContent = String(cart.item_count);
          node.style.display = '';
        } else {
          node.textContent = '';
          node.style.display = 'none';
        }
      });
    }

    onKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close();
      }
    }

    async onDocumentClick(evt) {
      const target = evt.target;
      if (!isElement(target)) return;

      const overlay = target.closest('[data-cart-drawer-overlay]');
      if (overlay && this.root.contains(overlay)) {
        evt.preventDefault();
        this.close();
        return;
      }

      const recoToggle = target.closest('.cart-drawer__recommendations-toggle');
      if (recoToggle && this.root.contains(recoToggle)) {
        evt.preventDefault();
        const wrap = recoToggle.closest('.cart-drawer__recommendations');
        if (wrap) {
          wrap.classList.toggle('active');
        }
        return;
      }

      const recoBtn = target.closest('.cart-drawer__reco-btn');
      if (recoBtn && this.root.contains(recoBtn) && recoBtn.tagName === 'BUTTON') {
        evt.preventDefault();
        this.onRecoAddClick(recoBtn);
        return;
      }

      const closeBtn = target.closest(this.closeSelector);
      if (closeBtn && this.root.contains(closeBtn)) {
        evt.preventDefault();
        this.close();
        return;
      }

      const removeBtn = target.closest('.cart-drawer__remove');
      if (removeBtn && this.root.contains(removeBtn)) {
        evt.preventDefault();
        this.onRemoveClick(removeBtn);
        return;
      }

      const qtyBtn = target.closest('.cart-drawer__qty-btn');
      if (qtyBtn && this.root.contains(qtyBtn)) {
        const deltaRaw = qtyBtn.getAttribute('data-qty-change');
        if (!deltaRaw) return;

        evt.preventDefault();
        this.onQtyButtonClick(qtyBtn);
        return;
      }

      const openTrigger = target.closest(this.openSelector);
      if (openTrigger) {
        if (openTrigger.hasAttribute('href')) {
          const triggerHref = openTrigger.getAttribute('href');
          const triggerUrl = new URL(triggerHref, window.location.origin);
          if (triggerUrl.href === window.location.href) {
            return;
          }
        }
        evt.preventDefault();
        try {
          await this.refreshBeforeOpen();
        } catch (err) {
          console.error(err);
        }
        this.open();
      }
    }

    async onThemeCartChanged() {
      if (this._suppressThemeCartChanged > 0) return;
      if (this._cartChangedRefreshPromise) return;

      this._cartChangedRefreshPromise = (async () => {
        await this.primeCartState();
        const prevCount = typeof this._lastItemCount === 'number' ? this._lastItemCount : null;

        const cart = await this.fetchCart();
        this.refreshCartCountFromCart(cart);

        const nextCount = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
        const didIncrease =
          typeof nextCount === 'number' && (prevCount == null ? nextCount > 0 : nextCount > prevCount);

        const shouldOpen = this.openOnAdd && didIncrease;
        if (shouldOpen) {
          this.open();
          await this.refreshSection();
          return;
        }

        if (this.root.classList.contains(this.openClass)) {
          await this.refreshSection();
        }
      })();

      try {
        await this._cartChangedRefreshPromise;
      } catch (err) {
        console.error(err);
      } finally {
        this._cartChangedRefreshPromise = null;
      }
    }

    async onCartUpdated(evt) {
      if (this._suppressCartUpdated > 0) return;
      if (this._cartChangedRefreshPromise) return;

      this._cartChangedRefreshPromise = (async () => {
        await this.primeCartState();
        const prevCount = typeof this._lastItemCount === 'number' ? this._lastItemCount : null;

        const detail = evt && evt.detail;
        const canUpdateFromDetail = detail && typeof detail.item_count === 'number';
        let nextCount = null;

        if (canUpdateFromDetail) {
          nextCount = detail.item_count;
          this.refreshCartCountFromCart(detail);
        } else {
          const cart = await this.fetchCart();
          this.refreshCartCountFromCart(cart);
          nextCount = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
        }

        const didIncrease =
          typeof nextCount === 'number' && (prevCount == null ? nextCount > 0 : nextCount > prevCount);

        const shouldOpen = this.openOnAdd && didIncrease;
        if (shouldOpen) {
          this.open();
          await this.refreshSection();
          return;
        }

        if (this.root.classList.contains(this.openClass)) {
          await this.refreshSection();
        }
      })();

      try {
        await this._cartChangedRefreshPromise;
      } catch (err) {
        console.error(err);
      } finally {
        this._cartChangedRefreshPromise = null;
      }
    }

    async refreshBeforeOpen() {
      if (this._openRefreshPromise) return this._openRefreshPromise;

      this._openRefreshPromise = (async () => {
        const cart = await this.fetchCart();
        this.refreshCartCountFromCart(cart);
        await this.refreshSection();
      })();

      try {
        await this._openRefreshPromise;
      } finally {
        this._openRefreshPromise = null;
      }
    }

    async onRemoveClick(button) {
      if (!isElement(button)) return;

      const key = button.dataset.key;
      const lineRaw = button.dataset.line;
      const line = Number(lineRaw);
      const hasValidLine = Boolean(line) && !Number.isNaN(line);
      const hasKey = Boolean(key);

      if (!hasKey && !hasValidLine) {
        console.error('Cart drawer remove: missing data-key and invalid data-line');
        return;
      }

      setLoadingState(button, true);
      this._suppressCartUpdated += 1;

      try {
        const baseUrl = getCartChangeUrl();
        const url = baseUrl.endsWith('.js') ? baseUrl : `${baseUrl}.js`;

        const body = new URLSearchParams();
        if (hasKey) {
          body.set('id', String(key));
        } else {
          body.set('line', String(line));
        }
        body.set('quantity', '0');

        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body
        });

        if (!res.ok) {
          let errorPayload = '';
          try {
            errorPayload = await res.text();
          } catch (e) {
            errorPayload = '';
          }
          throw new Error(`Remove from cart failed: ${res.status}${errorPayload ? ` - ${errorPayload}` : ''}`);
        }

        await res.json();

        await this.refreshCartCount();
        await this.refreshSection();

        document.documentElement.dispatchEvent(
          new CustomEvent('cart-drawer:removed', { bubbles: true })
        );
      } catch (err) {
        console.error(err);
        document.documentElement.dispatchEvent(
          new CustomEvent('cart-drawer:error', { bubbles: true, detail: { error: err } })
        );
      } finally {
        this._suppressCartUpdated = Math.max(0, this._suppressCartUpdated - 1);
        this._suppressThemeCartChanged = Math.max(0, this._suppressThemeCartChanged - 1);
        setLoadingState(button, false);
      }
    }

    async onFormSubmit(evt) {
      const form = evt.target;
      if (!isElement(form) || form.tagName !== 'FORM') return;

      let isAddToCartForm = false;
      try {
        isAddToCartForm = form.matches(this.addFormSelector);
      } catch (err) {
        const actionAttr = form.getAttribute('action') || '';
        const actionPath = actionAttr.split('?')[0];
        isAddToCartForm = actionPath.startsWith('/cart/add');
      }
      if (!isAddToCartForm) return;

      // Hybrid: if theme's AJAX add-to-cart is enabled on an ancestor container,
      // let the theme handler own the submission and only react to events.
      const ajaxContainer = form.closest('[data-ajax-add-to-cart="true"]');
      if (ajaxContainer) return;

      evt.preventDefault();

      const action = form.getAttribute('action') || '/cart/add';
      const url = action.endsWith('.js') ? action : `${action}.js`;

      const submitControls = Array.from(
        form.querySelectorAll('button[type="submit"], input[type="submit"]')
      ).filter((el) => isElement(el));

      submitControls.forEach((control) => {
        setLoadingState(control, true);
      });

      this._suppressCartUpdated += 1;
      this._suppressThemeCartChanged += 1;

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new FormData(form)
        });

        if (!res.ok) {
          throw new Error(`Add to cart failed: ${res.status}`);
        }

        await res.json();

        if (this.updateCount) {
          this.refreshCartCount();
        }

        await this.refreshSection();

        if (this.openOnAdd) {
          this.open();
        }

        document.documentElement.dispatchEvent(
          new CustomEvent('cart-drawer:added', { bubbles: true })
        );
      } catch (err) {
        console.error(err);
        document.documentElement.dispatchEvent(
          new CustomEvent('cart-drawer:error', { bubbles: true, detail: { error: err } })
        );
      } finally {
        this._suppressCartUpdated = Math.max(0, this._suppressCartUpdated - 1);
        this._suppressThemeCartChanged = Math.max(0, this._suppressThemeCartChanged - 1);
        submitControls.forEach((control) => {
          setLoadingState(control, false);
        });
      }
    }

    async refreshCartCount() {
      const cart = await this.fetchCart();
      this.refreshCartCountFromCart(cart);
    }

    async refreshSection() {
      if (!this.sectionId) return;

      const base = this.renderUrl || window.location.href;
      const currentUrl = new URL(base, window.location.origin);
      currentUrl.searchParams.set('sections', this.sectionId);
      currentUrl.searchParams.set('_', String(Date.now()));

      const res = await fetch(currentUrl.toString(), {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        cache: 'no-store'
      });

      if (!res.ok) {
        throw new Error(`Section refresh failed: ${res.status}`);
      }

      const sections = await res.json();

      const html = sections[this.sectionId];
      if (!html) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newRoot = doc.querySelector(`${SELECTOR_ROOT}[data-section-id="${CSS.escape(this.sectionId)}"]`);
      if (!newRoot) return;

      this.root.innerHTML = newRoot.innerHTML;

      if(typeof BOLD === 'object' && BOLD.common && BOLD.common.eventEmitter && typeof BOLD.common.eventEmitter.emit === 'function') {
        BOLD.common.eventEmitter.emit("BOLD_COMMON_cart_loaded");
      }

      this.setupOfferCarouselObserver();
    }
  }

  function init() {
    if (!window.AB_TEST_ENABLED) {
      return;
    }

    installCartInterceptors();

    const root = document.querySelector(SELECTOR_ROOT);
    if (!root) return;

    if (root.__cartDrawerInstance) {
      root.__cartDrawerInstance.destroy();
    }

    root.__cartDrawerInstance = new CartDrawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
